import { useEffect, useRef, useState } from "react";
import { Check, Copy, Link2, Share2, Users } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { normaliseOwnerName } from "../lib/url";
import type { UiLang } from "../lib/types";

interface Props {
  /** Hele lenka til turen, ferdig bygget. Se `shareUrl` i lib/url.ts. */
  url: string;
  /** Navnet som står på turen nå. Tom streng når ingen har oppgitt noe. */
  ownerName: string;
  onNameChange: (name: string) => void;
  /** Navnet på den som delte turen, når lenka kom fra noen andre. */
  sharedBy: string;
  lang: UiLang;
}

/**
 * Å dele turen med noen.
 *
 * ── Hva som alt fantes ─────────────────────────────────────────────────────
 *
 * Alt. Hele turen har ligget i URL-en siden lib/url.ts ble skrevet — ruta,
 * dagene, datoen, kjøretøyet, værvalgene og hvert stopp som er lagt til eller
 * fjernet — og `syncUrl` holder adresselinjen oppdatert ved hver endring. En
 * plan har altså vært delbar hele tiden, og `hasSharedForm` har hele tiden
 * kunnet se at en lenke kom utenfra.
 *
 * To ting manglet, og ingen av dem var motoren:
 *
 *   1. **Ingenting sa det.** Delingen forutsatte at brukeren gjettet at
 *      adresselinjen betydde noe. Det gjør folk ikke.
 *   2. **Lenka var anonym.** En URL med tolv parametre i en samtale sier
 *      ingenting om hvem som sendte den eller hva den er.
 *
 * ── Hvorfor «endre» ikke er samredigering ──────────────────────────────────
 *
 * Mottakeren kan endre turen — den lastes inn i deres eget skjema, og alt
 * virker. Det de ikke kan, er å skrive tilbake til *din* plan: appen har ingen
 * server som holder tilstand, og det er med vilje (se README). Modellen er
 * derfor kopi-og-send-tilbake framfor to markører i samme dokument, og
 * `sharedByNote` sier det rett ut. Alternativet — å la det se ut som
 * samredigering — ville betydd at to personer trodde de så det samme mens de
 * satt på hver sin plan.
 *
 * ── Navnet ─────────────────────────────────────────────────────────────────
 *
 * Frivillig, og tomt som standard. Det havner i en lenke som kan sendes
 * videre, og det er ikke appen som skal bestemme at man vil stå oppført på
 * den. Eieformen kommer fra ordboka og ikke fra en apostrof her: «Sushants
 * tur» på norsk, «Lars' tur» når navnet ender på s, «Le voyage de Sushant» på
 * fransk. Se `tripOwnedBy`.
 */
export function ShareTrip({ url, ownerName, onNameChange, sharedBy, lang }: Props) {
  const t = dictionaries[lang];
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  /*
   * Lenka er skjult til den trengs.
   *
   * Meldt fra bruk: skjul lenka, ha bare en delingsknapp, spør om navn, og si
   * at den er kopiert. Et readonly URL-felt er teknisk ærlig og visuelt støy —
   * ingen leser en URL, de limer den inn.
   *
   * `open` er trinnet der navnet spørres om. `failed` avslører feltet likevel:
   * `navigator.clipboard` finnes ikke i en usikker kontekst og kan nektes av
   * brukeren, og en mislykket kopiering uten lenke å ta med er en blindvei.
   * Det er hele grunnen feltet ikke er slettet.
   */
  const [open, setOpen] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);

  /*
   * «Kopiert» er en kvittering, ikke en tilstand. Uten dette står den til
   * neste klikk, og et merke som aldri forsvinner slutter å bety noe.
   */
  useEffect(() => {
    if (state === "idle") return;
    const timer = window.setTimeout(() => setState("idle"), 4000);
    return () => window.clearTimeout(timer);
  }, [state]);

  /*
   * Lenka endrer seg hver gang turen gjør det, og da er en gammel kvittering
   * en løgn om hva som ligger på utklippstavla.
   */
  useEffect(() => setState("idle"), [url]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setState("copied");
    } catch {
      /*
       * Utklippstavla krever tillatelse, og i Safari og i private vinduer kan
       * den nektes. Da merkes lenka i feltet i stedet, slik at ctrl+C virker —
       * en knapp som ikke gjør noe og ikke sier noe er verre enn ingen knapp.
       */
      setState("failed");
      linkRef.current?.select();
    }
  }

  return (
    <section className="card share" aria-labelledby="share-title">
      <h3 className="card-title" id="share-title">
        <Users size={15} />
        {t.shareTitle}
      </h3>

      {/*
        Beskjeden til den som kom inn via en lenke.

        Står øverst, før feltene, fordi den svarer på spørsmålet man har når
        siden åpner seg med en ferdig tur man ikke har laget: hvem sin er
        dette, og hva skjer om jeg rører den.
      */}
      {sharedBy && (
        <div className="share-from">
          <p className="share-from-lead">{t.sharedByLead(sharedBy)}</p>
          <p className="share-from-note">{t.sharedByNote}</p>
        </div>
      )}

      {/*
        Trinn 1: én knapp. Navnet og lenka kommer først når noen faktisk vil
        dele — det er to felt mindre på en side som alt har mange.
      */}
      {!open && (
        <button
          type="button"
          className="generate share-start"
          onClick={() => setOpen(true)}
        >
          <Share2 size={14} />
          {t.shareStart}
        </button>
      )}

      {open && (
        <>
      <label className="share-name">
        <span>{t.shareNameLabel}</span>
        <input
          type="text"
          value={ownerName}
          placeholder={t.shareNamePlaceholder}
          // Samme rensing som lenka leses med, så feltet og URL-en er enige.
          onChange={(event) => onNameChange(normaliseOwnerName(event.target.value))}
          maxLength={24}
        />
      </label>

      {/*
        Feltet er der bare når kopieringen sviktet — se `open` over. Uten det
        ville et mislykket klikk vært en blindvei uten lenke å ta med.
      */}
      {state === "failed" && (
      <div className="share-link">
        <Link2 size={14} aria-hidden="true" />
        <input
          ref={linkRef}
          type="text"
          className="share-link-field"
          value={url}
          readOnly
          // Et klikk i feltet merker hele lenka: ingen skal dra for å velge en URL.
          onFocus={(event) => event.target.select()}
          /*
           * Eget navn, ikke seksjonens.
           *
           * Feltet hadde `aria-label={t.shareTitle}`, som er den samme teksten
           * `<h3>`-en over gir seksjonen gjennom `aria-labelledby`. To
           * elementer med samme tilgjengelige navn er ikke bare tvetydig for en
           * test som leter etter ett av dem — det er tvetydig for den som
           * navigerer med skjermleser og hører «Del turen» to ganger uten å
           * vite hvilken av dem som er et felt.
           */
          aria-label={t.shareLinkLabel}
        />
      </div>
      )}

      {/*
        Knappeteksten står fast; kvitteringen står under.
        
        Begge sa «Lenken er kopiert» en stund, og det er den samme setningen to
        steder — støy for den som ser, og to treff for en skjermleser som leter
        etter statusen. En knappetekst som skifter mening er dessuten dårlig
        vane: knappen gjør det samme etter et klikk som før. Haken gir den
        umiddelbare bekreftelsen, `role="status"` under gir den man kan lese.
      */}
      <button type="button" className="share-copy" onClick={copy}>
        {state === "copied" ? <Check size={13} /> : <Copy size={13} />}
        {t.shareCopy}
      </button>

      {/*
        Kvitteringen som tekst, ikke bare som et ikon i knappen.

        `role="status"` gjør at en skjermleser sier den uten at fokus flyttes,
        og feilmeldingen trengs mest av den som nettopp fikk et klikk som ikke
        gjorde noe synlig.
      */}
      {/*
        Kvitteringen som tekst, ikke bare som et ikon i knappen. `role="status"`
        gjør at en skjermleser sier den uten at fokus flyttes — og «kopiert» er
        hele svaret nå, siden lenka ikke står der å se på.
      */}
      <p className="share-status" role="status">
        {state === "copied" ? t.shareCopied : state === "failed" ? t.shareCopyFailed : ""}
      </p>
        </>
      )}

      <p className="share-hint">{t.shareHint}</p>
    </section>
  );
}
